import {
  FaNetworkWired,
  FaDesktop,
  FaLaptop,
  FaServer,
  FaVideo,
  FaCamera,
  FaTools,
  FaCog,
} from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook
function useOnScreen(ref, rootMargin = "0px") {
  // Returns true if element is on screen
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

const services = [
  {
    icon: <FaNetworkWired />,
    title: "NETWORKING AMC",
    description:
      "Comprehensive Annual Maintenance Contracts (AMC) for all kinds of network infrastructure including installation, maintenance and quick troubleshooting for seamless connectivity.",
  },
  {
    icon: <FaDesktop />,
    title: "COMPUTER AMC",
    description:
      "Expert Computer AMC services providing scheduled maintenance, repairs, and emergency assistance to ensure your desktops run smoothly at all times.",
  },
  {
    icon: <FaLaptop />,
    title: "LAPTOP AMC",
    description:
      "Keep your laptops performing at their best with our AMC plans covering hardware, software, and preventive checks, tailored to your organization’s needs.",
  },
  {
    icon: <FaServer />,
    title: "SERVER AMC",
    description:
      "Reliable AMC for servers, including monitoring, backup, upgrades, and disaster recovery services handled by experienced professionals.",
  },
  {
    icon: <FaVideo />,
    title: "PROJECTOR AMC",
    description:
      "Professionally managed AMC services for projectors, ensuring high performance, brightness and longevity through regular upkeep.",
  },
  {
    icon: <FaCamera />,
    title: "CCTV AMC",
    description:
      "Annual Maintenance Contracts for all types of CCTV systems, including proactive inspections, repairs, upgrades, and 24/7 response.",
  },
  {
    icon: <FaTools />,
    title: "CCTV INSTALLATION",
    description:
      "Secure your premises with expert CCTV installation, tailored site surveys, optimal camera placement, and robust configuration for home or business.",
  },
  {
    icon: <FaCog />,
    title: "SERVICE & ALL IT SERVICES",
    description:
      "From IT consulting to emergency repairs and routine servicing—your complete IT partner for all troubleshooting, upgrades, and support requirements.",
  },
];

export default function RepairServiceSection() {
  const sectionRef = useRef(null);
  const isVisible = useOnScreen(sectionRef, "-80px");
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isVisible) setAnimate(true);
  }, [isVisible]);

  // We'll use a much higher initial translate-y (e.g. 80px) and a longer duration (2.5s)
  // We'll apply the same philosophy for all big up transitions
  return (
    <section
      ref={sectionRef}
      className="bg-gray-100 py-20 font-PTSerif-Regular"
    >
      <div className="max-w-7xl mx-auto px-6 flex flex-col gap-16 font-PTSerif-Regular">
        {/* IMAGES ON TOP */}
        <div
          className={`
            grid grid-cols-2 lg:grid-cols-3 gap-6 mb-8 font-PTSerif-Regular
            transition-all
            duration-[2500ms]
            ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}
          `}
        >
          <img
            src="/img/img1.jpeg"
            alt="Network/Computer Repair"
            className="w-full h-[220px] md:h-[260px] object-cover font-PTSerif-Regular rounded-xl shadow"
            style={{
              transition: "box-shadow 0.7s, transform 2.5s cubic-bezier(.2,1,.2,1)",
              boxShadow: animate ? "0px 12px 32px rgba(70,90,140,.1)" : "none",
            }}
          />
          <img
            src="/img/img5.jpeg"
            alt="Laptop/Server Service"
            className="w-full h-[220px] md:h-[260px] object-cover lg:mt-16 font-PTSerif-Regular rounded-xl shadow"
            style={{
              transition: "box-shadow 0.8s, transform 2.5s cubic-bezier(.2,1,.2,1) 0.22s",
              boxShadow: animate ? "0px 12px 32px rgba(70,90,140,.1)" : "none",
              transitionDelay: animate ? "0.22s" : "0s",
            }}
          />
          <img
            src="/img/img2.jpeg"
            alt="CCTV/Projector Install"
            className="w-full h-[220px] md:h-[260px] object-cover col-span-2 lg:col-span-1 font-PTSerif-Regular rounded-xl shadow"
            style={{
              transition: "box-shadow 0.8s, transform 2.5s cubic-bezier(.2,1,.2,1) 0.44s",
              boxShadow: animate ? "0px 12px 32px rgba(70,90,140,.12)" : "none",
              transitionDelay: animate ? "0.44s" : "0s",
            }}
          />
        </div>

        {/* DESCRIPTION & SERVICES GRID */}
        <div
          className={`
            font-PTSerif-Regular
            transition-all
            duration-[2600ms]
            ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-32"}
          `}
          style={{ transitionDelay: animate ? "0.5s" : "0s" }}
        >
          <h2 className={`
            text-4xl font-bold text-gray-900 leading-tight font-PTSerif-Regular
            transition-all
            duration-[2200ms]
            ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}
          `}
          style={{ transitionDelay: animate ? "0.8s" : "0s" }}
          >
            OUR IT SERVICES <br /> EXPERT PROFESSIONAL SOLUTIONS
          </h2>

          <p className={`
            text-gray-600 mt-6 leading-relaxed font-PTSerif-Regular
            transition-all
            duration-[2200ms]
            ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}
          `}
            style={{ transitionDelay: animate ? "1s" : "0s" }}
          >
            We provide a full range of AMC, installation, and IT support services for Networking, Computers, Laptops, Servers, Projectors, CCTV and more. Get hassle-free maintenance and expert solutions from certified professionals—ensuring your business always runs smoothly.
          </p>

          {/* Services List in TWO COLUMNS */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 font-PTSerif-Regular">
            {services.map((service, idx) => (
              <div
                key={service.title}
                className={`
                  flex gap-4 mb-4 font-PTSerif-Regular
                  transition-all
                  duration-[2000ms]
                  ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}
                `}
                style={{
                  transitionDelay: animate
                    ? `${1.3 + idx * 0.13}s`
                    : "0s",
                }}
              >
                <div className="text-blue-500 text-xl mt-1 font-PTSerif-Regular">{service.icon}</div>
                <div className="font-PTSerif-Regular">
                  <h4 className="font-semibold text-lg text-gray-800 font-PTSerif-Regular">{service.title}</h4>
                  <div className="w-10 h-[2px] bg-blue-500 my-2 font-PTSerif-Regular"></div>
                  <p className="text-gray-600 text-sm font-PTSerif-Regular">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}